import { useSyncExternalStore } from 'react'
import services from '../services'

/* Estado do layout novo, compartilhado pela casca, pelo menu de dashboards e pela tela de
   marcas: carrega uma vez por sessão e avisa quem estiver ouvindo quando muda. */

export interface Marca {
  id: number
  nome: string
  corPrincipal: string
  corApoio: string
  fonte: string
  logoClaro?: string | null
  logoEscuro?: string | null
  ambientes: number[]
}

export interface Favorito {
  dashboardId: number
  ambienteId: number
}

export interface DashboardNaArvore {
  /** Tem agente do oneflow ligado neste ambiente. */
  oneflow?: boolean
  id: number
  nome: string
  tipo: 'powerbi' | 'html'
  ordem: number
  atualizarACada?: number | null
  painelDeFiltros: boolean
  navegacao: boolean
}

export interface GrupoNaArvore {
  id: number
  nome: string
  ordem: number
  dashboards: DashboardNaArvore[]
}

export interface AmbienteNaArvore {
  id: number
  nome: string
  marcaId?: number | null
  grupos: GrupoNaArvore[]
}

export interface AmbienteResumo {
  id: number
  name: string
  brandId?: number | null
}

interface Estado {
  carregado: boolean
  marcas: Marca[]
  preferencias: Record<string, string>
  favoritos: Favorito[]
  ambientes: AmbienteResumo[]
  /** Ambientes, grupos e dashboards da pessoa; nulo até a primeira vez que alguém precisa. */
  arvore: AmbienteNaArvore[] | null
  erroArvore: string | null
}

let estado: Estado = { carregado: false, marcas: [], preferencias: {}, favoritos: [], ambientes: [], arvore: null, erroArvore: null }
const ouvintes = new Set<() => void>()
let carregando: Promise<void> | null = null

function mudar(parcial: Partial<Estado>) {
  estado = { ...estado, ...parcial }
  ouvintes.forEach(f => f())
}

function assinar(f: () => void) {
  ouvintes.add(f)
  return () => { ouvintes.delete(f) }
}

export function useNovoPortal(): Estado {
  return useSyncExternalStore(assinar, () => estado)
}

export function carregarNovoPortal(forcar = false): Promise<void> {
  if (carregando && !forcar) return carregando
  carregando = (async () => {
    const [ctx, amb] = await Promise.all([services.api.novoPortalContexto(), services.api.environmentsRelatedAll()])
    if (ctx.fail) {
      carregando = null
      return
    }
    const preferencias = ctx.data.preferencias || {}
    mudar({
      carregado: true,
      marcas: ctx.data.marcas || [],
      preferencias,
      favoritos: ctx.data.favoritos || [],
      ambientes: amb.fail ? [] : (amb.data || []).map((a: any) => ({ id: a.id, name: a.name, brandId: a.brandId }))
    })
  })()
  return carregando
}

/** Esquece o que foi carregado (ex.: ao sair), para a próxima pessoa não ver os dados da anterior. */
export function limparNovoPortal() {
  carregando = null
  carregandoArvore = null
  arvoreEm = 0
  arvoreVelha = false
  mudar({ carregado: false, marcas: [], preferencias: {}, favoritos: [], ambientes: [], arvore: null, erroArvore: null })
}

let carregandoArvore: Promise<void> | null = null
let arvoreNoAr = false
/** Quando a última carga boa terminou. */
let arvoreEm = 0
/** Algo foi gravado desde a última carga: o que está em memória pode estar velho. */
let arvoreVelha = false

/** Quanto tempo a árvore vale: depois disso, a próxima tela que a pedir busca de novo. */
const VALIDADE_DA_ARVORE_MS = 60_000

// Dashboard, vínculo, ambiente, grupo, acesso: qualquer coisa que se grave pode mudar o que a árvore mostra.
services.api.quandoGravar?.(() => { arvoreVelha = true })

/**
 * Carrega a árvore da pessoa; a busca, o seletor de ambiente e a tela do dashboard usam a mesma. Quem pede de novo depois de
 * algo ter sido gravado, ou depois de um minuto, recebe a árvore atualizada: a que está na tela continua lá até a nova chegar.
 */
export function carregarArvore(forcar = false): Promise<void> {
  const velha = arvoreVelha || (arvoreEm > 0 && Date.now() - arvoreEm > VALIDADE_DA_ARVORE_MS)
  if (carregandoArvore && !forcar && !(velha && !arvoreNoAr)) return carregandoArvore
  arvoreNoAr = true
  arvoreVelha = false
  carregandoArvore = (async () => {
    const res = await services.api.novoPortalArvore()
    arvoreNoAr = false
    if (res.fail) {
      carregandoArvore = null
      // Com uma árvore já na tela, uma falha ao atualizar não a apaga nem assusta.
      if (!estado.arvore) mudar({ erroArvore: res.error || 'Não foi possível carregar seus dashboards.' })
      return
    }
    arvoreEm = Date.now()
    mudar({ arvore: res.data || [], erroArvore: null })
  })()
  return carregandoArvore
}

export function ehFavorito(e: Estado, dashboardId: number, ambienteId: number): boolean {
  return e.favoritos.some(f => f.dashboardId === dashboardId && f.ambienteId === ambienteId)
}

/** Marca ou desmarca na hora e desfaz se o servidor recusar. Devolve a mensagem de erro, se houver. */
export async function alternarFavorito(dashboardId: number, ambienteId: number): Promise<string | null> {
  const era = ehFavorito(estado, dashboardId, ambienteId)
  const anterior = estado.favoritos
  mudar({
    favoritos: era
      ? anterior.filter(f => !(f.dashboardId === dashboardId && f.ambienteId === ambienteId))
      : [...anterior, { dashboardId, ambienteId }]
  })
  const res = era
    ? await services.api.novoPortalDesfavoritar(dashboardId, ambienteId)
    : await services.api.novoPortalFavoritar(dashboardId, ambienteId)
  if (res.fail) {
    mudar({ favoritos: anterior })
    return res.error || 'Não foi possível salvar o favorito.'
  }
  return null
}

/** Depois de salvar marcas na gestão, a casca recarrega para vestir as cores novas. */
export function atualizarMarcas(marcas: Marca[], ambientes?: AmbienteResumo[]) {
  mudar({ marcas, ...(ambientes ? { ambientes } : {}) })
}
